import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BillingInfo } from '@/components/BillingPage';
import { authenticatedFetch } from '@/utils/api';
import { API_URLS } from '@/constants';

export const useHeaderData = () => {
  const { isLoggedIn, logout, user } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [userBilling, setUserBilling] = useState<BillingInfo>();

  // Validate subscription
  const checkSubscription = async () => {
    try {
      const subscriptionResponse = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_USER_SUBSCRIPTION);
      if (subscriptionResponse.ok) {
        const subscriptionData = await subscriptionResponse.json();
        setUserBilling(subscriptionData.subscription);
      } else {
        // No subscription found → default to Free plan
        setUserBilling({
          id: undefined,
          planName: 'Free',
          billingPeriod: 'monthly',
          nextBillingDate: '',
          amount: 0,
          currency: 'USD',
          status: 'active'
        });
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      // Fallback to Free plan on error
      setUserBilling({
        id: undefined,
        planName: 'Free',
        billingPeriod: 'monthly',
        nextBillingDate: '',
        amount: 0,
        currency: 'USD',
        status: 'active'
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Only check subscription if user is logged in
    if (isLoggedIn) {
      checkSubscription();
    }
  }, [isLoggedIn]);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase())
        .slice(0, 2)
        .join('');
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return {
    dropdownRef,
    isDropdownOpen,
    isLoggedIn,
    userBilling,
    checkSubscription,
    logout,
    getUserInitials,
    setIsDropdownOpen,
  };
};
